// A bottom sheet — the phone frame's answer to "let me read that properly".
//
// WHY IT EXISTS. On a phone a card in the Select rack shows its name, its two banded figures, its
// numbers and one clamped line of gloss. Everything else — coverage, conditions, the effect text —
// is only reachable by PICKING the card, because the full rules render in the pair tray below the
// rack. So the phone flow was "commit to a choice in order to read it, then undo if you were
// wrong". This is the surface that lets a player read first.
//
// CREATED ON OPEN, REMOVED ON CLOSE, and that is a hard requirement rather than a preference. The
// stacked phone page has exactly one scroller, the document, and tests/browser/phone-check.js scans
// every painted region for the combination that broke it three times: an internal scroller that
// also refuses to chain (`overscroll-behavior: contain`). This sheet's body is deliberately BOTH —
// which is correct for a modal, because the page behind it is not meant to pan — so leaving it in
// the DOM `display: none` would park a permanent trap in every phase for the scan to find. It is
// not hidden between uses; it does not exist.
//
// NOT A <dialog>. showModal() gives the focus trap and the backdrop for free, and it also gives a
// top-layer element whose backdrop cannot be styled consistently across the browsers this has to
// run in from file://. The trap here is twenty lines and behaves the same everywhere.
window.UISheet = (function () {
  'use strict';

  var ID = 'ui-sheet';
  var lastFocus = null;

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/[&<>"]/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
      });
  }

  function node() { return document.getElementById(ID); }
  function isOpen() { return !!node(); }

  // Visible focusables only: an actions row can legitimately render nothing, and a trap that
  // cycles through a zero-size button strands the keyboard on something the player cannot see.
  function focusables() {
    var el = node();
    if (!el) return [];
    var all = el.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),' +
      'textarea:not([disabled]),[tabindex]:not([tabindex="-1"])');
    return Array.prototype.filter.call(all, function (n) {
      var r = n.getBoundingClientRect();
      return r.width > 0 || r.height > 0;
    });
  }

  function onKeydown(ev) {
    if (!isOpen()) return;
    if (ev.key === 'Escape') { ev.preventDefault(); close(); return; }
    if (ev.key !== 'Tab') return;
    var f = focusables();
    if (!f.length) { ev.preventDefault(); return; }
    var first = f[0], last = f[f.length - 1];
    // The trap only has to act at the two ends; everywhere else the browser's own order is right.
    if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); }
    else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); }
  }

  function close() {
    var el = node();
    if (!el) return;
    if (el.parentNode) el.parentNode.removeChild(el);
    document.removeEventListener('keydown', onKeydown, true);
    // Back to whatever opened it. Guarded on the node still being in the document: the sheet's own
    // action can re-render the screen underneath it (selecting a card redraws the rack), and
    // focusing a detached element throws away the focus entirely rather than restoring it.
    if (lastFocus && document.contains(lastFocus)) {
      try { lastFocus.focus(); } catch (e) { /* focus is best-effort, never fatal */ }
    }
    lastFocus = null;
  }

  // opts: { title, bodyHtml, actions: [{ label, kind: 'primary'|'ghost', onClick }] }
  // `bodyHtml` is trusted markup from the caller — every current caller builds it with the same
  // escaping renderers the rest of the board uses.
  function open(opts) {
    opts = opts || {};
    close();                                  // one sheet at a time, never two stacked
    lastFocus = document.activeElement;

    var wrap = document.createElement('div');
    wrap.id = ID;
    wrap.className = 'ui-sheet';
    wrap.innerHTML =
      '<div class="ui-sheet-backdrop" data-role="backdrop"></div>' +
      '<div class="ui-sheet-panel" role="dialog" aria-modal="true" aria-label="' +
        esc(opts.title || 'Details') + '">' +
        '<div class="ui-sheet-head">' +
          '<span class="ui-sheet-grip" aria-hidden="true"></span>' +
          '<h2 class="ui-sheet-title">' + esc(opts.title || '') + '</h2>' +
          '<button type="button" class="ui-sheet-close" data-role="close" aria-label="Close">' +
            '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
            '<path d="M6 6 L18 18 M18 6 L6 18" fill="none" stroke="currentColor" ' +
            'stroke-width="2.2" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="ui-sheet-body" data-role="body"></div>' +
        '<div class="ui-sheet-actions" data-role="actions"></div>' +
      '</div>';

    var body = wrap.querySelector('[data-role="body"]');
    body.innerHTML = opts.bodyHtml || '';

    var actions = wrap.querySelector('[data-role="actions"]');
    var list = opts.actions || [];
    if (!list.length) actions.classList.add('is-empty');
    list.forEach(function (a) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'sheet-action' + (a.kind === 'primary' ? ' primary' : ' ghost');
      b.textContent = a.label || '';
      b.onclick = function () {
        // The handler runs AFTER the sheet is gone, so an action that re-renders the screen
        // underneath is not rendering behind a modal that is about to remove itself.
        var fn = a.onClick;
        close();
        if (typeof fn === 'function') fn();
      };
      actions.appendChild(b);
    });

    wrap.querySelector('[data-role="backdrop"]').onclick = close;
    wrap.querySelector('[data-role="close"]').onclick = close;

    document.body.appendChild(wrap);
    document.addEventListener('keydown', onKeydown, true);

    // Focus lands on the close control rather than on the first thing in the body: it is the one
    // action present in every sheet, and it puts the escape route under the very first Tab.
    var closeBtn = wrap.querySelector('[data-role="close"]');
    if (closeBtn) { try { closeBtn.focus(); } catch (e) { /* best-effort */ } }
    return wrap;
  }

  return { open: open, close: close, isOpen: isOpen };
})();
